// src/screens/Resources/DownloadResourcesPage.jsx

import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  LockKeyhole,
  Mail,
  Share2,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import resourceApi from "api/resource/resoure.api";

import { downloadResources, resourceText } from "i18n/resource";
import {
  backdropVariants,
  cardVariants,
  fadeUpVariants,
  modalVariants,
  pageContainerVariants,
} from "animations/variants";
import usePageSEO from "hook/UsePageSEO";
import ShareModal from "components/Blog/ShareModal";

const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const DownloadResourcesPage = () => {
  const navigate = useNavigate();

  const lang = useSelector((state) => state.language?.lang || "en");

  const t = resourceText[lang] || resourceText.en;

  const seo = {
    en: {
      title: "Free Career Resources | Tiffany Trâm Bùi",
      description:
        "Download free career guides and practical resources for building your career in New Zealand.",
    },
    vi: {
      title: "Tài liệu hướng nghiệp miễn phí | Tiffany Trâm Bùi",
      description:
        "Tải miễn phí các tài liệu hướng nghiệp giúp bạn xây dựng sự nghiệp tại New Zealand.",
    },
  };

  const s = seo[lang] || seo.en;

  const shareUrl = "https://tbcareer.com/free-download";
  const imagePath = "https://tbcareer.com/download/downloadpreview.png";

  usePageSEO({
    title: s.title,
    description: s.description,
    ogTitle: s.title,
    ogDescription: s.description,
    ogImage: imagePath,
    ogUrl: shareUrl,
    ogSite_name: "TB Career",
  });

  const [selectedResource, setSelectedResource] = useState(null);

  const [email, setEmail] = useState("");

  const [marketingConsent, setMarketingConsent] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [openShare, setOpenShare] = useState(false);

  const resetModal = () => {
    setSelectedResource(null);
    setEmail("");
    setMarketingConsent(false);
    setSubmitting(false);
  };

  const closeModal = () => {
    if (submitting) return;

    resetModal();
  };

  useEffect(() => {
    if (!selectedResource) return undefined;

    const previousOverflow = document.body.style.overflow;

    const handleEscape = (event) => {
      if (event.key === "Escape" && !submitting) {
        closeModal();
      }
    };

    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleEscape);
    };
  }, [selectedResource, submitting]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedResource || submitting) return;

    const normalizedEmail = email.trim().toLowerCase();

    if (!isValidEmail(normalizedEmail)) {
      toast.error(t.validEmail);
      return;
    }

    setSubmitting(true);

    try {
      const { response, err } = await resourceApi.requestDownload({
        email: normalizedEmail,
        resourceSlug: selectedResource.slug,
        marketingConsent,
      });

      if (err) {
        toast.error(err.response?.data?.message || t.failedMessage);

        return;
      }

      toast.success(response?.data?.message || t.sentMessage, {
        duration: 6000,
      });

      resetModal();
    } catch (error) {
      console.error("Resource download request failed:", error);

      toast.error(error.response?.data?.message || t.failedMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen overflow-hidden bg-darkbg">
      {/* Hero */}
      <section className="relative flex h-[430px] items-center justify-center overflow-hidden text-center text-white lg:h-[500px]">
        <motion.div
          initial={{
            scale: 1.1,
          }}
          animate={{
            scale: 1,
          }}
          transition={{
            duration: 1.4,
            ease: "easeOut",
          }}
          className="absolute inset-0 bg-cover bg-center grayscale"
          style={{
            backgroundImage: "url(/images/tb1.jpg)",
          }}
        />

        <div className="absolute inset-0 bg-navy/75" />

        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-navy/70" />

        <motion.div
          variants={pageContainerVariants}
          initial="hidden"
          animate="show"
          className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-6 pt-16"
        >
          <motion.p
            variants={fadeUpVariants}
            className="mb-4 text-xs font-bold uppercase tracking-[0.28em] text-sky-300 md:text-sm"
          >
            {t.eyebrow}
          </motion.p>

          <motion.h1
            variants={fadeUpVariants}
            className="max-w-3xl font-heading text-3xl font-bold leading-tight tracking-wide md:text-5xl lg:text-6xl"
          >
            {t.heroTitle}
          </motion.h1>

          <motion.p
            variants={fadeUpVariants}
            className="mt-5 max-w-2xl text-sm leading-7 text-white/80 md:text-base"
          >
            {t.heroDescription}
          </motion.p>

          <motion.div
            variants={fadeUpVariants}
            className="mt-7 inline-flex flex-wrap items-center gap-2 rounded-full border border-white/40 bg-white/10 px-5 py-2.5 text-sm text-white backdrop-blur-md"
          >
            <button
              type="button"
              onClick={() => navigate("/")}
              className="transition hover:text-sky-300"
            >
              {t.home}
            </button>

            <span className="opacity-60">»</span>

            <span className="font-medium text-sky-200">{t.resources}</span>
          </motion.div>
        </motion.div>
      </section>

      {/* Resources */}
      <section className="px-5 py-16 md:px-6 md:py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div
            variants={fadeUpVariants}
            initial="hidden"
            whileInView="show"
            viewport={{
              once: true,
              amount: 0.3,
            }}
            className="mx-auto mb-14 max-w-3xl text-center"
          >
            {/* Eyebrow */}
            <div className="mb-4 flex items-center justify-center gap-3">
              <span className="h-px w-8 bg-amber/70" />

              <p className="text-xs font-bold uppercase tracking-[0.24em] text-navylight md:text-sm">
                {t.sectionEyebrow}
              </p>

              <span className="h-px w-8 bg-amber/70" />
            </div>

            {/* Heading */}
            <h2 className="font-heading text-3xl font-bold leading-tight tracking-tight text-navy md:text-4xl lg:text-5xl">
              {t.sectionTitle}
            </h2>

            {/* Description */}
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-slate-600 md:text-base md:leading-8">
              {t.sectionDescription}
            </p>

            {/* Share button */}
            <div className="mt-7 flex justify-center">
              <motion.button
                type="button"
                onClick={() => setOpenShare(true)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
                aria-label="Share free career resources"
                className="
        group inline-flex items-center justify-center gap-2
        rounded-full border border-navy/15 bg-white
        px-5 py-2.5 text-sm font-semibold text-navy
        shadow-sm transition
        hover:border-amber hover:bg-amber/10 hover:shadow-md
        focus:outline-none focus:ring-2 focus:ring-amber/50
        focus:ring-offset-2
      "
              >
                <Share2
                  size={17}
                  strokeWidth={2}
                  className="transition-transform duration-200 group-hover:-translate-y-0.5"
                />

                <span>{lang === "vi" ? "Chia sẻ" : "Share "}</span>
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            variants={pageContainerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{
              once: true,
              amount: 0.15,
            }}
            className="grid gap-7 md:grid-cols-2"
          >
            {downloadResources.map((resource, index) => {
              const content = resource[lang] || resource.en;

              return (
                <motion.article
                  key={resource.slug}
                  variants={cardVariants}
                  whileHover={{
                    y: -8,
                  }}
                  transition={{
                    duration: 0.25,
                  }}
                  className="group relative overflow-hidden rounded-3xl border border-navy/10 bg-white shadow-[0_16px_45px_rgba(15,60,110,0.08)]"
                >
                  <div className="h-1.5 w-full bg-gradient-to-r from-navy via-navylight to-navymid" />

                  <div className="relative p-7 md:p-9">
                    <div className="absolute right-0 top-0 h-36 w-36 -translate-y-1/2 translate-x-1/2 rounded-full bg-navylight/10 transition duration-500 group-hover:scale-125" />

                    <div className="relative">
                      <div className="mb-7 flex items-start justify-between gap-4">
                        <motion.div
                          whileHover={{
                            rotate: -5,
                            scale: 1.05,
                          }}
                          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-navy text-white shadow-lg shadow-navy/20"
                        >
                          <FileText className="h-8 w-8" />
                        </motion.div>

                        <span className="rounded-full border border-navylight/20 bg-navylight/10 px-3 py-1.5 text-xs font-bold text-navylight">
                          {content.category}
                        </span>
                      </div>

                      <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-navylight">
                        {t.pdfLabel}
                      </p>

                      <h3 className="font-heading text-2xl font-bold leading-snug text-navy md:text-3xl">
                        {content.title}
                      </h3>

                      <p className="mt-4 min-h-[84px] text-sm leading-7 text-slate-600 md:text-base">
                        {content.description}
                      </p>

                      <div className="mt-6 space-y-3 border-t border-slate-100 pt-6">
                        {[t.featureOne, t.featureTwo, t.featureThree].map(
                          (feature) => (
                            <div
                              key={feature}
                              className="flex items-center gap-3 text-sm text-slate-600"
                            >
                              <CheckCircle2 className="h-4 w-4 shrink-0 text-navylight" />

                              <span>{feature}</span>
                            </div>
                          ),
                        )}
                      </div>

                      <motion.button
                        type="button"
                        whileTap={{
                          scale: 0.97,
                        }}
                        onClick={() => setSelectedResource(resource)}
                        className="mt-8 inline-flex w-full items-center justify-between rounded-xl bg-navy px-5 py-4 font-semibold text-white transition hover:bg-navylight sm:w-auto sm:min-w-[220px]"
                      >
                        <span className="flex items-center gap-2">
                          <Download className="h-5 w-5" />

                          {t.downloadButton}
                        </span>

                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                      </motion.button>
                    </div>
                  </div>

                  <div className="h-2 bg-navylight/10">
                    <motion.div
                      initial={{
                        width: "0%",
                      }}
                      whileInView={{
                        width: index === 0 ? "72%" : "88%",
                      }}
                      viewport={{
                        once: true,
                      }}
                      transition={{
                        duration: 1,
                        delay: 0.3,
                      }}
                      className="h-full bg-navylight"
                    />
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Download modal */}
      <AnimatePresence>
        {selectedResource && (
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeModal();
              }
            }}
            className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-navy/80 px-4 py-8 backdrop-blur-md"
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="download-modal-title"
              className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              {/* Modal header */}
              <div className="relative overflow-hidden bg-navy px-7 py-7 text-white md:px-8">
                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-navylight/30" />

                <div className="absolute -bottom-16 right-20 h-32 w-32 rounded-full bg-white/5" />

                <button
                  type="button"
                  onClick={closeModal}
                  disabled={submitting}
                  aria-label={t.close}
                  title={t.close}
                  className="absolute right-5 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="relative pr-10">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 text-sky-200">
                    <Mail className="h-6 w-6" />
                  </div>

                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-200">
                    {t.modalEyebrow}
                  </p>

                  <h2
                    id="download-modal-title"
                    className="font-heading text-2xl font-bold md:text-3xl"
                  >
                    {t.modalTitle}
                  </h2>
                </div>
              </div>

              {/* Modal form */}
              <div className="p-7 md:p-8">
                <p className="text-sm leading-6 text-slate-600">
                  {t.modalDescription}
                </p>

                <div className="mt-3 rounded-xl border border-navylight/20 bg-navylight/10 px-4 py-3">
                  <p className="font-semibold leading-6 text-navy">
                    {selectedResource[lang]?.title || selectedResource.en.title}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="mt-6">
                  <label
                    htmlFor="resource-email"
                    className="mb-2 block text-sm font-semibold text-navy"
                  >
                    {t.emailLabel}
                  </label>

                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-navymid" />

                    <input
                      id="resource-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder={t.emailPlaceholder}
                      autoComplete="email"
                      autoFocus
                      required
                      disabled={submitting}
                      className="w-full rounded-xl border border-slate-300 bg-white py-3.5 pl-12 pr-4 text-navy outline-none transition placeholder:text-slate-400 focus:border-navylight focus:ring-4 focus:ring-navylight/10 disabled:cursor-not-allowed disabled:bg-slate-100"
                    />
                  </div>

                  <label className="mt-5 flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(event) =>
                        setMarketingConsent(event.target.checked)
                      }
                      disabled={submitting}
                      className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 accent-navylight disabled:cursor-not-allowed"
                    />

                    <span className="text-sm leading-6 text-slate-600">
                      {t.consent}
                    </span>
                  </label>

                  <div className="mt-5 flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3">
                    <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-navylight" />

                    <p className="text-xs leading-5 text-slate-500">
                      {t.privacy}
                    </p>
                  </div>

                  <motion.button
                    type="submit"
                    whileTap={
                      submitting
                        ? undefined
                        : {
                            scale: 0.98,
                          }
                    }
                    disabled={submitting}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-navylight px-5 py-4 font-bold text-white shadow-lg shadow-navylight/20 transition hover:bg-navy disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {t.preparing}
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5" />
                        {t.submitButton}
                      </>
                    )}
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ShareModal
        open={openShare}
        onClose={() => setOpenShare(false)}
        url={shareUrl}
      />
    </main>
  );
};

export default DownloadResourcesPage;
